import React, { memo, useCallback, useEffect, useState } from 'react';
import { PDFWidgetForm } from './PDFBlock';
import { FormInput } from '@core/components/form';
import Dropzone from 'react-dropzone';
import { UploadAPI } from '@business/api/upload';
import { tap } from 'rxjs/operators';
import { Label } from 'reactstrap';
import { FileAPI } from '@business/api/file';
import { FileResponse } from '@business/entities/file';
import { useProposalDataContext } from '@modules/proposal/ProposalBuilderPage';
import { UploadNote } from '../Common/UploadNote';

export interface PDFBlockSettingsProps {
  form: PDFWidgetForm;
}

export const PDFBlockSettings = memo((props: PDFBlockSettingsProps) => {
  const { form } = props;
  const organization = useProposalDataContext()?.organization;
  const [designFiles, setDesignFiles] = useState<FileResponse[]>([]);

  // A proposal built for a customer can attach that customer's Design Files (PDF blueprints)
  // instead of re-uploading them. Fetched across all of the customer's folders; nothing renders in
  // the plain template builder, which has no customer.
  useEffect(() => {
    if (!organization?.id) return;
    const subscription = FileAPI.search(organization.id, '', undefined, 'design').subscribe(response =>
      setDesignFiles(response.items.filter(item => item.type === 'file') as FileResponse[]),
    );
    return () => subscription.unsubscribe();
  }, [organization?.id]);

  const onDrop = useCallback(
    (files: File[]) => {
      if (!files) return null;
      if (files.length == 0) return null;

      files.forEach(file => {
        UploadAPI.upload('proposals', file)
          .pipe(tap(res => form.patchValue({ file: res })))
          .subscribe();
      });
    },
    [form],
  );
  return (
    <div>
      <FormInput control={form.controls.height} type="number" />

      {!!designFiles.length && (
        <div className="mb-3">
          <Label>{`${organization?.name || 'Customer'} Design Files`}</Label>
          {designFiles.map(file => (
            <a
              key={file.id}
              className="d-block text-truncate hover-pointer"
              onClick={() => form.patchValue({ file: { fileName: file.name, fileUrl: file.url, filePath: '', fulePath: '' } })}
            >
              <i className="bx bxs-file-pdf text-danger mr-1" />
              {file.name}
            </a>
          ))}
        </div>
      )}

      <div>
        <Dropzone onDrop={onDrop} maxFiles={1} accept="application/pdf">
          {({ getRootProps, getInputProps }) => (
            <div className="dropzone">
              <div className="dz-message needsclick" {...getRootProps()}>
                <input {...getInputProps()} />
                <div className="dz-message needsclick">
                  <div className="mb-3">
                    <i className="display-4 text-muted bx bxs-cloud-upload" />
                  </div>
                  <h4>Drop files here</h4>
                </div>
              </div>
            </div>
          )}
        </Dropzone>
        <UploadNote kind="pdf" className="mt-2" />
      </div>
    </div>
  );
});
